import { renderList as _renderList, Fragment as _Fragment, openBlock as _openBlock, createElementBlock as _createElementBlock, resolveComponent as _resolveComponent, vShow as _vShow, createBlock as _createBlock, withDirectives as _withDirectives, normalizeClass as _normalizeClass, createElementVNode as _createElementVNode, unref as _unref, createVNode as _createVNode, createCommentVNode as _createCommentVNode, withModifiers as _withModifiers } from "vue"

const _hoisted_1 = {
  key: 0,
  class: "center-absolutely-fixed w-lg z-20 p-6 text-center bg-white border-2 border-dashed border-gray-brand-500 rounded-md"
}

import { computed, nextTick, onMounted, reactive, ref, watch } from 'vue'
import { useRoute } from 'vue-router'
import { useStore } from 'vuex'

import newImageIcon from '@/icon/NewImageIcon'
import api from '@/store/api'
import { mapRelationship } from '@/store/mappers'

const pageSize = 20


export default {
  __name: 'MessageStream',
  props: {
  channel: { type: Object, required: true }
},
  emits: ['submit-files', 'scroll-position-changed', 'new-message-while-scrolled'],
  setup(__props, { expose: __expose, emit: __emit }) {

const route = useRoute()
const store = useStore()

const props = __props

const emit = __emit

const box = ref(null)

const data = reactive({
  enterTarget: null,
  isDragging: false,
  loadingMore: false,
  page: 0,
  prevHeight: null,
  keepLoading: true,
  isInitialLoad: true,
  isScrolledToBottom: true,
  newMessageCount: 0
})

const pendingMessages = computed(() => store.state.pendingMessages)
const loggedInUser = computed(() => mapRelationship({ type: 'user', id: store.getters.loggedInUserId }))

const conversationItems = computed(() => {
  const conversationId = mapRelationship({ type: 'conversation_user_pair', id: route.params.id })?.attributes
    .conversation_id
  const conversationItems = Object.values(store.state.objects.conversation_item || {}).filter(
    x => x.attributes.conversation_id === conversationId
  )

  if (!conversationItems) return []
  return conversationItems.sort((a, b) => new Date(a.attributes.created_at) - new Date(b.attributes.created_at))
})

const messageIdForItem = item => {
  return item?.relationships?.message?.data?.id || null
}

const highlightClasses = ['ring-2', 'ring-brand-300', 'ring-offset-2']
const lastHighlightedMessageId = ref(null)

const scrollToMessage = () => {
  const id = route.query.messageId
  if (!id) return
  if (!box.value) return
  if (lastHighlightedMessageId.value === id) return

  const el = box.value.querySelector(`[data-message-id="${id}"]`)
  if (!el) return

  lastHighlightedMessageId.value = id
  el.scrollIntoView({ behavior: 'smooth', block: 'center' })
  el.classList.add(...highlightClasses)
  setTimeout(() => {
    el.classList.remove(...highlightClasses)
  }, 2000)
}

const checkScrollPosition = () => {
  if (!box.value) return

  const threshold = 50
  const isAtBottom = box.value.scrollTop + box.value.clientHeight >= box.value.scrollHeight - threshold
  const wasAtBottom = data.isScrolledToBottom

  data.isScrolledToBottom = isAtBottom

  if (isAtBottom && data.newMessageCount > 0) {
    data.newMessageCount = 0
  }

  if (wasAtBottom !== isAtBottom) {
    emit('scroll-position-changed', isAtBottom)
  }
}

watch(
  pendingMessages,
  () =>
    nextTick(() => {
      if (box.value) {
        box.value.scrollTop = box.value.scrollHeight
        data.isScrolledToBottom = true
        data.newMessageCount = 0
        emit('scroll-position-changed', true)
      }
    }),
  { deep: true }
)

watch(
  conversationItems,
  (newItems, oldItems) => {
    nextTick(() => {
      if (box.value && (data.isInitialLoad || !data.loadingMore)) {
        if (data.isInitialLoad) {
          setTimeout(() => {
            if (box.value) box.value.scrollTop = box.value.scrollHeight
          }, 500)
        } else if (oldItems && newItems.length > oldItems.length) {
          const newMessages = newItems.slice(oldItems.length)
          const newMessagesFromOthers = newMessages.filter(item => {
            if (item.type === 'message') {
              const conversationItemUser = mapRelationship(item.relationships.user.data)
              return conversationItemUser.attributes.user_type !== 'merchant'
            }
            return true
          })

          const newMessagesFromMerchant = newMessages.filter(item => {
            if (item.type === 'message') {
              const conversationItemUser = mapRelationship(item.relationships.user.data)
              return conversationItemUser.attributes.user_type === 'merchant'
            }
            return false
          })

          if (newMessagesFromMerchant.length > 0) {
            box.value.scrollTop = box.value.scrollHeight
            data.isScrolledToBottom = true
            data.newMessageCount = 0
            emit('scroll-position-changed', true)
          } else if (newMessagesFromOthers.length > 0) {
            if (data.isScrolledToBottom) {
              box.value.scrollTop = box.value.scrollHeight
            } else {
              data.newMessageCount += newMessagesFromOthers.length
              emit('new-message-while-scrolled', data.newMessageCount)
            }
          }
        }
      }
      data.isInitialLoad = false
    })
  },
  { deep: true }
)

watch(
  [conversationItems, () => route.query.messageId],
  () => {
    if (!route.query.messageId) return
    nextTick(() => {
      // Defer slightly so any pending scroll-to-bottom from the initial load
      // settles before we scroll the target message into view.
      setTimeout(scrollToMessage, 250)
    })
  },
  { immediate: true, deep: true }
)

const getConversation = id =>
  Promise.all([
    api.call('GET', `conversation_user_pairs/${id}`),
    api.call('GET', `conversation_user_pairs/${id}/conversation_items?limit=${pageSize}&page=0`).then(response => {
      data.page = 0
      data.keepLoading = response.data.data.length === pageSize
    })
  ])

onMounted(() => {
  getConversation(route.params.id)
  api.call('POST', `conversation_user_pairs/${route.params.id}/read`)

  setTimeout(() => {
    if (box.value) box.value.scrollTop = box.value.scrollHeight
  }, 500)

  box.value.addEventListener('scroll', async () => {
    checkScrollPosition()

    data.prevHeight = box.value.scrollHeight
    if (!data.keepLoading || data.loadingMore || box.value.scrollTop > 1000) return

    data.loadingMore = true
    const response = await api.call(
      'GET',
      `conversation_user_pairs/${route.params.id}/conversation_items?limit=${pageSize}&page=${++data.page}`
    )
    data.loadingMore = false

    data.keepLoading = response.data.data.length === pageSize
    if (box.value.scrollTop === 0) {
      nextTick(() => {
        box.value.scrollTop = box.value.scrollHeight - data.prevHeight
      })
    }
  })

  props.channel.bind('created-conversation-item', data => {
    store.commit('RECEIVE_OBJECT', data)
    if (data.data.relationships.message) {
      const message = mapRelationship(data.data.relationships.message.data)
      store.commit('REMOVE_PENDING_MESSAGE', message.attributes.object_key)
    }
  })
  props.channel.bind('updated-conversation-item', data => {
    store.commit('RECEIVE_OBJECT', data)
    if (data.data.relationships.message) {
      const message = mapRelationship(data.data.relationships.message.data)
      store.commit('REMOVE_PENDING_MESSAGE', message.attributes.object_key)
    }
  })
  props.channel.bind('updated-conversation-user-pair', pair => {
    store.commit('RECEIVE_OBJECT', pair)
    if (box.value) box.value.scrollTop = box.value.scrollHeight
    api.call('POST', `conversation_user_pairs/${route.params.id}/read`)
  })
})

const dragEnter = event => {
  data.enterTarget = event.target
  data.isDragging = true
}
const dragOver = event => {
  data.isDragging = true
}
const dragLeave = event => {
  if (data.enterTarget === event.target) {
    data.isDragging = false
  }
}
const dropHandler = event => {
  emit('submit-files', event.dataTransfer.files)
  data.isDragging = false
}

const scrollToBottom = () => {
  if (box.value) {
    box.value.scrollTop = box.value.scrollHeight
    data.newMessageCount = 0
    data.isScrolledToBottom = true
    emit('scroll-position-changed', true)
  }
}

__expose({
  scrollToBottom
})

return (_ctx, _cache) => {
  const _component_conversation_item = _resolveComponent("conversation-item")
  const _component_pending_message = _resolveComponent("pending-message")

  return (_openBlock(), _createElementBlock("div", {
    ref_key: "box",
    ref: box,
    class: "pb-6 px-6",
    onDrop: _withModifiers(dropHandler, ["prevent"]),
    onDragenter: _withModifiers(dragEnter, ["prevent"]),
    onDragover: _withModifiers(dragOver, ["prevent"]),
    onDragleave: _withModifiers(dragLeave, ["prevent"])
  }, [
    _createElementVNode("div", {
      class: _normalizeClass(["space-y-3 pt-4", data.isDragging && 'opacity-20'])
    }, [
      (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(conversationItems.value, (item, index) => {
        return _withDirectives((_openBlock(), _createBlock(_component_conversation_item, {
          key: item.id,
          item: item,
          previousItem: conversationItems.value[index - 1] || null,
          "data-message-id": messageIdForItem(item)
        }, null, 8, ["item", "previousItem", "data-message-id"])), [
          [_vShow, conversationItems.value.length]
        ])
      }), 128)),
      (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(pendingMessages.value, (message, key) => {
        return (_openBlock(), _createBlock(_component_pending_message, {
          key: key,
          message: message,
          user: loggedInUser.value
        }, null, 8, ["message", "user"]))
      }), 128))
    ], 2),
    (data.isDragging)
      ? (_openBlock(), _createElementBlock("div", _hoisted_1, [
          _createVNode(_unref(newImageIcon), { class: "mx-auto w-12 h-12 text-gray-brand-600" }),
          _cache[0] || (_cache[0] = _createElementVNode("p", { class: "mt-1 text-gray-brand-800" }, "Drag and drop", -1)),
          _cache[1] || (_cache[1] = _createElementVNode("p", { class: "mt-1 text-gray-brand-600 text-sm" }, "PNG, JPG, GIF up to 10 MB", -1))
        ]))
      : _createCommentVNode("", true)
  ], 544))
}
}

}